import { DemoBadge } from '../learning/DemoBadge'
import { SUBJECTS } from '../learning/subjects'
import { type DemoRunReport, HELP_LABELS } from '../materials/model'
import {
  observedLine,
  patternLine,
  questionOriginLabel,
  sourceLabel,
  summarizeRun,
} from '../materials/report'
import { PARENT_NO_MASTERY_TEXT, STATUS_TEXT } from '../materials/ui'

/**
 * Parent view of the last DEMO school-material run on this device (Slice 2,
 * D-14.4 shape): source, subject and topic, each answer, the error patterns
 * and the next action, with facts, interpretation and evidence status kept
 * apart. The run lives in memory only and carries no learner identifier, so
 * it is shown as "a sample try on this device", never as a child's result.
 */

export const DEMO_RUN_NOTE =
  'Lượt thử với tài liệu mẫu trên thiết bị này, chỉ giữ đến khi tải lại trang. Không gắn với hồ sơ bé nào và không phải kết quả học tập thật.'

export const DEMO_RUN_PROFILE = 'không gắn với hồ sơ nào (lượt thử trên thiết bị này)'

export function MaterialRunPreview({
  run,
}: {
  run: DemoRunReport | null
}): React.JSX.Element {
  return (
    <section
      aria-labelledby="material-run"
      className="flex flex-col gap-4 rounded-2xl border-2 border-dashed border-fuchsia-300 p-4 dark:border-fuchsia-800"
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <DemoBadge />
          <h2 id="material-run" className="text-lg font-semibold">
            Xem trước: học từ tài liệu
          </h2>
        </div>
        <p className="text-slate-700 dark:text-slate-300">{DEMO_RUN_NOTE}</p>
        <p>
          <span className="font-semibold">Hồ sơ: </span>
          {DEMO_RUN_PROFILE}
        </p>
      </div>
      {run === null ? (
        <p>
          Chưa có lượt thử nào. Trong chế độ học, chọn “Tài liệu ở trường” → “Thử tài liệu
          mẫu” và làm hết phần luyện tập, rồi quay lại đây.
        </p>
      ) : (
        <RunDetail run={run} />
      )}
    </section>
  )
}

function RunDetail({ run }: { run: DemoRunReport }): React.JSX.Element {
  const summary = summarizeRun(run.topic, run.attempts)
  const subject = SUBJECTS.find((s) => s.code === run.sample.subject)

  return (
    <>
      <dl className="grid gap-2 md:grid-cols-3">
        <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-900">
          <dt className="text-sm text-slate-600 dark:text-slate-400">Tài liệu (mẫu)</dt>
          <dd className="font-semibold">{sourceLabel(run.sample)}</dd>
        </div>
        <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-900">
          <dt className="text-sm text-slate-600 dark:text-slate-400">Môn · Chủ đề</dt>
          <dd className="font-semibold">
            {subject?.name} · {run.topic.title}
          </dd>
        </div>
        <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-900">
          <dt className="text-sm text-slate-600 dark:text-slate-400">Mục tiêu</dt>
          <dd>{run.topic.objective}</dd>
        </div>
      </dl>

      <section aria-labelledby="run-observed" className="flex flex-col gap-2">
        <h3 id="run-observed" className="font-semibold">
          Quan sát (câu trả lời thực tế của lượt thử)
        </h3>
        <p>{observedLine(summary)}</p>
        <ol className="flex flex-col gap-1 text-sm">
          {run.topic.questions.map((q, i) => {
            const a = run.attempts.find((x) => x.questionId === q.id)
            return (
              <li
                key={q.id}
                className="rounded-lg border border-slate-200 p-2 dark:border-slate-800"
              >
                <p>
                  Câu {i + 1}. <span lang={q.lang}>{q.prompt}</span>
                </p>
                <p className="text-slate-600 dark:text-slate-400">
                  {questionOriginLabel(run.sample, q)}
                </p>
                <p className="text-slate-700 dark:text-slate-300">
                  {a === undefined
                    ? 'Chưa làm'
                    : `Trả lời: ${a.display} — ${a.correct ? 'đúng' : `chưa đúng (đáp án: ${a.correctDisplay})`}${
                        a.helpBeforeSubmit.length > 0
                          ? ` · có trợ giúp: ${a.helpBeforeSubmit.map((m) => HELP_LABELS[m]).join(', ')}`
                          : ''
                      }`}
                </p>
              </li>
            )
          })}
        </ol>
      </section>

      <section aria-labelledby="run-interpretation" className="flex flex-col gap-1">
        <h3 id="run-interpretation" className="font-semibold">
          Nhận định (theo quy tắc mẫu, không do AI tạo)
        </h3>
        <p>{STATUS_TEXT[summary.status]}</p>
        {summary.patterns.map((p) => (
          <p key={p.tag} className="text-sm">
            Kiểu lỗi {patternLine(run.topic, p)}
          </p>
        ))}
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Chỉ câu tự làm (không mở trợ giúp) được dùng để nhận định.{' '}
          {PARENT_NO_MASTERY_TEXT}
        </p>
      </section>

      <div className="rounded-xl bg-sky-50 p-3 dark:bg-sky-950/40">
        <p className="font-semibold">Việc nên làm tiếp: {summary.nextAction.text}</p>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Vì: {summary.nextAction.reason}
        </p>
      </div>
    </>
  )
}
